// Frozen-tree renderer: draws demoted (frozen) plant cells as tapered wood segments and
// needle sprigs instead of one sphere per cell.
//
// Human-facing only. The quadruped never sees any of this; it perceives through its own
// sensors. Everything here is derived from the frozen wire records (see FROZEN_RECORD_BYTES)
// and never feeds back to the sim.
//
// Two instanced draw layers, each split by space and then into fixed-size chunks:
//   bins    the world is cut into BIN_M metre squares; every cell lives in the bin under it, and
//           each bin has its own wood pool and sprig pool. A pool knows the bounds of what it
//           holds and hands that bounding sphere to its chunks, so a bin outside the view
//           frustum costs no vertex work at all, and far bins can be thinned (see SPRIG_LOD).
//           Before bins, chunks filled in arrival order, so every chunk held trees from all over
//           the world, none could be culled, and the vertex shader ran for the whole forest
//           every frame, including everything behind the camera.
//   chunks  fixed-size (CHUNK instances) so growth never reallocates a GPU buffer (a
//           reallocation would strand the old buffer until the GL context dies).
//   wood    a 6-sided tapered prism per stem / branch / twig cell (twigs in their own pool, hidden far away), running from the cell's
//           parent to the cell. Vertices are placed in the vertex shader from per-instance
//           endpoints and radii, so the mesh stays one small shared geometry.
//   sprigs  three crossed alpha-tested cards carrying a procedural needle spray. One per
//           leaf cell, per twig, and on the outer half of boughs. Lit with an up-biased
//           normal so crowns read as volumes with bright tops and dark undersides, rather
//           than as a field of flat cards.
//
// Girth. Frozen records are immutable, so a tree's thickening with age cannot be written
// into them. Instead the store tracks how far each tree's leader has progressed (the
// highest stem `prog` seen for that owner) and derives a per-tree girth factor, uploaded as
// a 4096x1 float texture the wood shader samples per instance. Fattening a whole tree costs
// one texel, not thousands of matrix rewrites.
//
// Sway. Trees move in the shared wind field (wind.ts), cosmetic and client-only. A second
// per-tree texture holds each tree's base and top height; the vertex shaders bend every
// endpoint by a function of its position and height fraction, so trunks stay planted, crowns
// move, and joints stay closed without any per-frame CPU work.
import * as THREE from 'three';
import { PALM_LEAF_URI, PALM_LEAF_MEAN, PALM_BARK_URI, PALM_BARK_MEAN } from './palmAssets';
import { WindField, WIND_GLSL } from './wind';
import { CanopyShade, CANOPY_SHADE_DECL, canopyShadeApply } from './canopyShade';

// Wire record, 36 bytes, little endian (must match broadcast_server._FROZEN_WIRE_DTYPE):
//   0 cid u32 | 4 owner u32 | 8 x f32 | 12 y f32 | 16 z f32
//   20 dx i16 | 22 dy i16 | 24 dz i16      parent position minus this one, millimetres
//   26 r0 u16 | 28 r1 u16                  radius at parent end / own end, 0.1 mm units
//   30 ctype u8 | 31 prog u8 | 32 r u8 | 33 g u8 | 34 b u8 | 35 flags u8
//      flags: bit0 = has parent, bits 1..3 = the plant's render kind (see below)
export const FROZEN_RECORD_BYTES = 36;

// Render kind (ecology Genome.kind), carried in flags bits 1..3. It decides how foliage is drawn:
//   conifer  needle sprigs of 0.2 to 0.4 m on the outer boughs and twigs (the original look)
//   palm     a bare trunk, and long pinnate fronds: every segment of a frond (a "branch" cell)
//            carries a big sprig that hangs down and out, so a frond reads as a feathered arc
//   scrub    small dense foliage along the whole stem and every bough, for low beach shrubs
export const KIND_CONIFER = 0;
export const KIND_PALM = 1;
export const KIND_SCRUB = 2;

export const CELL_STEM = 0;
export const CELL_BRANCH = 1;
export const CELL_LEAF = 2;
export const CELL_TWIG = 3;

const CHUNK_SHIFT = 12;
const CHUNK = 1 << CHUNK_SHIFT;  // instances per draw chunk (one pool per bin and layer, so chunks stay small)
const BIN_M = 16;               // metres per spatial bin side
const BOUNDS_MARGIN = 3.0;      // metres added to a pool's bounds: wind sway, needle reach, girth
// Distance LOD for needle sprigs. A bin whose nearest point is at least `from` metres from the
// camera draws only `frac` of its sprigs, each scaled up by `mul` so the crown keeps its
// weight (frac * mul^2 is about 0.9). Wood is never thinned: a missing branch reads as a
// hole, a missing needle card does not.
const SPRIG_LOD = [
  { from: 0,   frac: 1.0,  mul: 1.0 },
  { from: 55,  frac: 0.6,  mul: 1.25 },
  { from: 110, frac: 0.35, mul: 1.6 },
];
// Twig wood is hidden in bins farther than this. Twigs are the thinnest, most numerous wood (centimetre
// radii, so under a quarter of a pixel wide at this distance on a 1080p display) and the needle
// sprigs that sit on them are still drawn, so nothing visible is lost.
const THIN_WOOD_FAR = 60;
const LOD_HYST = 0.06;          // fraction of a threshold a bin must cross before its level changes
const GIRTH_TEX_W = 4096;       // max distinct trees with their own girth
const SEGMENT_SIDES = 6;
// Wood that casts into a shadow map: only segments thick enough to cover about half a texel (radius >= WOOD_SHADOW_TEXEL_K texels),
// since thinner wood is under a texel wide and only flickers. The limit follows the cascade being drawn, read from its projection:
// about 1 cm in the near cascade (so a palm's upper trunk and its rachises cast) up to WOOD_SHADOW_MIN_R in the far ones, where a texel
// is tens of centimetres. The flashlight's perspective shadow keeps WOOD_SHADOW_MIN_R. Rachises, scrub stems and twigs stay out of
// the far cascades, where they are the bulk of the wood.
// `?nowoodshadow` in the page URL turns the wood's shadows off, for comparing frame time.
const WOOD_SHADOW_MIN_R = 0.05;
const WOOD_SHADOW_MIN_R_FINE = 0.01;
const WOOD_SHADOW_TEXEL_K = 0.5;
const SHADOW_MAP_PX = 2048;

// Colours on the wire are sRGB bytes; the shaders convert them to linear (pow 2.2). Live
// particles in DefaultSimRenderer feed the same bytes in unconverted, so they read paler:
// deliberately, since pale = fresh growth.

// Look tuning. Sizes are metres (the world unit).
const GIRTH_MIN = 0.10;         // a seedling's girth as a fraction of the mature radius
const GIRTH_EXP = 0.9;          // girth ~ progress^exp
const GIRTH_RATE = 1.5;         // 1/s, how fast girth eases toward its target
export const ROOT_FLARE_DEPTH = 0.30;  // the root cell gets a stub this far below it (sinks into terrain)
export const ROOT_FLARE_SCALE = 1.6;
const LEAF_SPRIG_PER_RADIUS = 4.2;   // sprig length = leaf radius * this
const LEAF_SPRIG_MIN = 0.18;
const LEAF_SPRIG_MAX = 0.40;
const TWIG_SPRIG_MIN = 0.18;
const BRANCH_SPRIG_FROM = 0.25;      // boughs carry needles on the part beyond this progress
const STEM_SPRIG_FROM = 0.78;        // the upper trunk carries a few, hiding the bare leader
const STEM_SPRIG_CHANCE = 0.55;
// Palm fronds. A frond segment is about 0.19 m, so a sprig of 5 times that overlaps its neighbours
// into a continuous feathered blade; leaflets hang in a V either side of the rib (PALM_SHEET_HANG) like a coconut frond.
const PALM_SPRIG_PER_LEN = 3.4;
const PALM_SPRIG_MIN = 0.62;
const PALM_SPRIG_FROM = 0.04;        // fronds are leafy almost from the trunk
const PALM_LEAF_MIN = 0.50;          // the cells that cap a frond (and the spear leaf at the crown)
const PALM_LEAF_MAX = 1.00;
const PALM_LEAF_PER_RADIUS = 7.5;
const PALM_SHEET_WIDTH = 0.90;       // half-width of a frond card's leaflet sheet, as a fraction of its length
const PALM_SHEET_HANG = 0.85;        // how far the leaflets' outer edge hangs below the rib, per unit of width
// A frond is about ten segments (prog 0..255 along it) and a card spans PALM_CARD_SPAN of its length, so the cards of the last segments run
// past the rib's end. The sheet narrows from PALM_TIP_FROM to a point at PALM_TIP_END (in fractions of the frond), as a real frond's
// leaflets shorten to the tip, instead of every leaflet running full length to a flat cut.
const PALM_CARD_SPAN = 0.34;
const PALM_SEG_FRAC = 0.10;
const PALM_TIP_FROM = 0.76;
const PALM_TIP_END = 1.22;
const PALM_TIP_MIN = 0.06;
const PALM_FOLIAGE_PROG = 250;       // prog of a frond-foliage leaf cell: it carries shade only and is not drawn
const PALM_RACHIS_BARK_MIX = 0.20;   // fronds keep mostly their own olive colour, not bark brown
// Scrub: leaves all along the stem and boughs.
const SCRUB_SPRIG_FROM = 0.05;
const SCRUB_STEM_FROM = 0.12;
const SCRUB_STEM_CHANCE = 0.9;

// Needle sheen: sunlight glancing off the sun-facing side of the crown. Diffuse brightness is
// roughly even around the tree (it stands in for skylight, since there is no atmosphere yet);
// the sheen is added on top, driven by the crown normal (out from the trunk, tilted up).
const SHEEN_STRENGTH = 0.05;    // multiplies the sun colour*intensity; raise for a stronger glint
const SHEEN_POWER = 8.0;        // lobe tightness around the sun/eye half-vector; higher = smaller patch

// The server colours boughs and twigs with the species' foliage-ish `branch_color`, which
// reads as black-green sticks on bare wood. Wood is drawn as bark instead: a brown that
// takes on a little of that colour toward the outer twigs. (Stems keep their own colour.)
const BARK_R = 92, BARK_G = 68, BARK_B = 46;
const BRANCH_BARK_MIX_MIN = 0.10, BRANCH_BARK_MIX_MAX = 0.40, TWIG_BARK_MIX = 0.50;

// ---------------------------------------------------------------------------------------
// Hashing (stable per-cell randomness: a cell looks the same on every client and reload)
// ---------------------------------------------------------------------------------------
function hash01(cid: number, salt: number): number {
  let h = (Math.imul(cid | 0, 0x9e3779b1) ^ Math.imul(salt | 0, 0x85ebca6b)) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d) >>> 0;
  h ^= h >>> 12; h = Math.imul(h, 0x297a2d39) >>> 0;
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

// ---------------------------------------------------------------------------------------
// Chunked instance pool: 3 x vec3 float + 1 x rgba u8 per instance (40 bytes)
// One pool per (bin, layer). Instance data is world-space positions, so three's own bounds for
// the geometry (the unit tube or card) mean nothing; the pool keeps the real bounds instead and
// shares one bounding sphere with every chunk, which makes frustum culling work per bin.
// ---------------------------------------------------------------------------------------
interface Chunk {
  a: Float32Array; b: Float32Array; c: Float32Array; col: Uint8Array;
  attrs: THREE.InstancedBufferAttribute[];
  geo: THREE.InstancedBufferGeometry;
  mesh: THREE.Mesh;
  dirtyLo: number; dirtyHi: number;
}

class InstancePool {
  private chunks: Chunk[] = [];
  n = 0;
  /** instances submitted to the GPU after LOD thinning (before frustum culling, which three does) */
  drawn = 0;
  /** owner (cell index) of each instance, for swap-remove bookkeeping */
  private owner: Int32Array = new Int32Array(256);
  private material: THREE.Material;
  private frac = 1;
  private enabled = true;
  private readonly sphere = new THREE.Sphere();
  private readonly lo = new THREE.Vector3(Infinity, Infinity, Infinity);
  private readonly hi = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
  private boundsDirty = false;
  private readonly scene: THREE.Scene;
  private readonly base: THREE.BufferGeometry;
  private readonly onMove: (cell: number, newIdx: number) => void;
  /** wood instances are two endpoints; sprig instances are an anchor, a direction and a size */
  private readonly sprig: boolean;
  /** Only the wood pool has one: the depth material that places a segment in the sun's shadow pass (see makeWoodDepthMaterial). */
  private readonly depth: THREE.MeshDepthMaterial | null;
  private casts = false;

  constructor(scene: THREE.Scene, base: THREE.BufferGeometry, material: THREE.Material, sprig: boolean,
              onMove: (cell: number, newIdx: number) => void, depth: THREE.MeshDepthMaterial | null = null) {
    this.scene = scene; this.base = base; this.material = material; this.sprig = sprig; this.onMove = onMove;
    this.depth = depth;
  }

  /** Cast into the shadow maps or not. Only a pool built with a depth material can. */
  setCastShadow(on: boolean) {
    this.casts = on && this.depth !== null;
    for (const ch of this.chunks) ch.mesh.castShadow = this.casts;
  }

  get chunkCount(): number { return this.chunks.length; }
  /** nearest distance from `p` to this pool's bounds (Infinity when empty) */
  distanceTo(p: THREE.Vector3): number {
    if (this.n === 0 || this.lo.x > this.hi.x) return Infinity;
    const dx = Math.max(this.lo.x - p.x, 0, p.x - this.hi.x);
    const dy = Math.max(this.lo.y - p.y, 0, p.y - this.hi.y);
    const dz = Math.max(this.lo.z - p.z, 0, p.z - this.hi.z);
    return Math.hypot(dx, dy, dz);
  }

  private addChunk(): Chunk {
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = this.base.index;
    for (const name of Object.keys(this.base.attributes)) geo.setAttribute(name, this.base.attributes[name]);
    const a = new Float32Array(CHUNK * 3), b = new Float32Array(CHUNK * 3), c = new Float32Array(CHUNK * 3);
    const col = new Uint8Array(CHUNK * 4);
    const attrs = [
      new THREE.InstancedBufferAttribute(a, 3),
      new THREE.InstancedBufferAttribute(b, 3),
      new THREE.InstancedBufferAttribute(c, 3),
      new THREE.InstancedBufferAttribute(col, 4, true),
    ];
    for (const at of attrs) at.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aA', attrs[0]); geo.setAttribute('aB', attrs[1]);
    geo.setAttribute('aC', attrs[2]); geo.setAttribute('aCol', attrs[3]);
    geo.instanceCount = 0;
    geo.boundingSphere = this.sphere;   // shared with every chunk of the pool, kept current in flush()
    const mesh = new THREE.Mesh(geo, this.material);
    mesh.frustumCulled = true;          // against the pool's real bounds (see above)
    // Needles, twigs and the thin wood stay out of the shadow pass (the frozen set is its killer, see DefaultSimRenderer);
    // the wood pool casts, through a depth material that drops segments thinner than a shadow texel.
    mesh.castShadow = this.casts;
    if (this.depth) mesh.customDepthMaterial = this.depth;
    mesh.receiveShadow = false;
    mesh.visible = false;
    this.scene.add(mesh);
    const ch: Chunk = { a, b, c, col, attrs, geo, mesh, dirtyLo: Infinity, dirtyHi: -1 };
    this.chunks.push(ch);
    return ch;
  }

  private mark(ch: Chunk, local: number) {
    if (local < ch.dirtyLo) ch.dirtyLo = local;
    if (local > ch.dirtyHi) ch.dirtyHi = local;
  }

  private grow(x: number, y: number, z: number) {
    const lo = this.lo, hi = this.hi;
    if (x < lo.x) { lo.x = x; this.boundsDirty = true; } if (x > hi.x) { hi.x = x; this.boundsDirty = true; }
    if (y < lo.y) { lo.y = y; this.boundsDirty = true; } if (y > hi.y) { hi.y = y; this.boundsDirty = true; }
    if (z < lo.z) { lo.z = z; this.boundsDirty = true; } if (z > hi.z) { hi.z = z; this.boundsDirty = true; }
  }

  /** Append an instance owned by `cell`; returns its index in this pool. */
  add(cell: number): number {
    const idx = this.n++;
    const ci = idx >> CHUNK_SHIFT;
    while (ci >= this.chunks.length) this.addChunk();
    if (idx >= this.owner.length) {
      const o = new Int32Array(this.owner.length * 2); o.set(this.owner); this.owner = o;
    }
    this.owner[idx] = cell;
    return idx;
  }

  /** `flag` rides in the colour's alpha byte: 255 = ordinary, 128 = palm trunk bark (see makeWoodMaterial). */
  set(idx: number, a: ArrayLike<number>, b: ArrayLike<number>, c: ArrayLike<number>,
      r: number, g: number, bl: number, flag = 255) {
    const ch = this.chunks[idx >> CHUNK_SHIFT];
    const l = idx & (CHUNK - 1);
    const o = l * 3;
    ch.a[o] = a[0]; ch.a[o + 1] = a[1]; ch.a[o + 2] = a[2];
    ch.b[o] = b[0]; ch.b[o + 1] = b[1]; ch.b[o + 2] = b[2];
    ch.c[o] = c[0]; ch.c[o + 1] = c[1]; ch.c[o + 2] = c[2];
    const k = l * 4;
    ch.col[k] = r; ch.col[k + 1] = g; ch.col[k + 2] = bl; ch.col[k + 3] = flag;
    this.mark(ch, l);
    this.grow(a[0], a[1], a[2]);
    if (this.sprig) this.grow(a[0] + b[0] * c[0], a[1] + b[1] * c[0], a[2] + b[2] * c[0]);
    else this.grow(b[0], b[1], b[2]);
  }

  /** Swap-remove: the last instance moves into the hole. Bounds only ever grow (conservative) until the pool empties. */
  remove(idx: number) {
    const last = this.n - 1;
    if (idx !== last) {
      const s = this.chunks[last >> CHUNK_SHIFT], d = this.chunks[idx >> CHUNK_SHIFT];
      const sl = last & (CHUNK - 1), dl = idx & (CHUNK - 1);
      for (let i = 0; i < 3; i++) {
        d.a[dl * 3 + i] = s.a[sl * 3 + i]; d.b[dl * 3 + i] = s.b[sl * 3 + i]; d.c[dl * 3 + i] = s.c[sl * 3 + i];
      }
      for (let i = 0; i < 4; i++) d.col[dl * 4 + i] = s.col[sl * 4 + i];
      this.mark(d, dl);
      const moved = this.owner[last];
      this.owner[idx] = moved;
      this.onMove(moved, idx);
    }
    this.n = last;
    if (last === 0) { this.lo.set(Infinity, Infinity, Infinity); this.hi.set(-Infinity, -Infinity, -Infinity); }
  }

  /** Draw only this fraction of the pool (1 = all). Cheap; safe to call every frame. */
  setFrac(f: number) { if (f !== this.frac) { this.frac = f; this.sync(); } }
  setMaterial(m: THREE.Material) {
    if (m === this.material) return;
    this.material = m;
    for (const ch of this.chunks) ch.mesh.material = m;
  }
  setEnabled(e: boolean) { if (e !== this.enabled) { this.enabled = e; this.sync(); } }

  /** Set each chunk's draw count and visibility from the pool size, LOD fraction and enable flag. */
  private sync() {
    let drawn = 0;
    for (let ci = 0; ci < this.chunks.length; ci++) {
      const ch = this.chunks[ci];
      const count = Math.max(0, Math.min(CHUNK, this.n - ci * CHUNK));
      const d = this.frac >= 1 ? count : Math.ceil(count * this.frac);
      ch.geo.instanceCount = d;
      ch.mesh.visible = this.enabled && d > 0;
      drawn += d;
    }
    this.drawn = drawn;
  }

  /** Push dirty ranges to the GPU, refresh the bounding sphere and set draw counts. Call once per frame. */
  flush() {
    if (this.boundsDirty) {
      this.boundsDirty = false;
      if (this.lo.x <= this.hi.x) {
        this.sphere.center.copy(this.lo).add(this.hi).multiplyScalar(0.5);
        this.sphere.radius = 0.5 * this.hi.distanceTo(this.lo) + BOUNDS_MARGIN;
      }
    }
    for (let ci = 0; ci < this.chunks.length; ci++) {
      const ch = this.chunks[ci];
      if (ch.dirtyHi < ch.dirtyLo) continue;
      const lo = ch.dirtyLo, n = ch.dirtyHi - ch.dirtyLo + 1;
      for (let k = 0; k < ch.attrs.length; k++) {
        const at = ch.attrs[k] as any;
        const size = at.itemSize as number;
        // three >= r159 has addUpdateRange (ranges are cleared after upload); older
        // versions have a single updateRange object.
        if (typeof at.addUpdateRange === 'function') at.addUpdateRange(lo * size, n * size);
        else { at.updateRange.offset = lo * size; at.updateRange.count = n * size; }
        at.needsUpdate = true;
      }
      ch.dirtyLo = Infinity; ch.dirtyHi = -1;
    }
    this.sync();
  }

  clear() {
    this.n = 0;
    this.lo.set(Infinity, Infinity, Infinity); this.hi.set(-Infinity, -Infinity, -Infinity);
    this.boundsDirty = false;
    for (const ch of this.chunks) { ch.geo.instanceCount = 0; ch.mesh.visible = false; ch.dirtyLo = Infinity; ch.dirtyHi = -1; }
    this.drawn = 0;
  }

  dispose() {
    for (const ch of this.chunks) { this.scene.remove(ch.mesh); ch.geo.dispose(); }
    this.chunks.length = 0; this.n = 0; this.drawn = 0;
  }
}

/** One spatial bin: its wood pool, its sprig pool, and the sprig LOD level currently applied. */
interface Bin { id: number; wood: InstancePool; twigs: InstancePool; sprigs: InstancePool; level: number; twigsOn: boolean }

// ---------------------------------------------------------------------------------------
// Geometry and textures
// ---------------------------------------------------------------------------------------
/** Unit tube, `position` = (cos t, along 0..1, sin t). Open-ended: joints are hidden by overlap. */
export function makeSegmentGeometry(): THREE.BufferGeometry {
  const S = SEGMENT_SIDES;
  const pos = new Float32Array(S * 2 * 3);
  for (let i = 0; i < S; i++) {
    const t = (i / S) * Math.PI * 2;
    pos.set([Math.cos(t), 0, Math.sin(t)], i * 3);
    pos.set([Math.cos(t), 1, Math.sin(t)], (S + i) * 3);
  }
  const idx: number[] = [];
  for (let i = 0; i < S; i++) {
    const j = (i + 1) % S;
    idx.push(i, j, S + i, j, S + j, S + i);
  }
  // The shader overrides the normal, but the attribute must EXIST: without one three
  // silently switches the material to derivative-based flat shading, whose normals always
  // face the camera. That lit every segment as if it were facing the viewer, so highlights
  // landed on the wrong side relative to the sun.
  const nor = new Float32Array(S * 2 * 3);
  for (let i = 0; i < S * 2; i++) { nor[i * 3] = pos[i * 3]; nor[i * 3 + 2] = pos[i * 3 + 2]; }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  g.setIndex(idx);
  return g;
}

/** Three crossed cards along +Y (0..1) and +-0.5 wide, 60 degrees apart. Attributes: position, normal (local), uv. */
function makeSprigGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
  for (let p = 0; p < 3; p++) {
    const a = (p / 3) * Math.PI;
    const cx = Math.cos(a), sz = Math.sin(a);         // card's width axis in the XZ plane
    const nx = -sz, nz = cx;                          // its normal
    const base = pos.length / 3;
    for (const [u, v] of [[-0.5, 0], [0.5, 0], [0.5, 1], [-0.5, 1]] as const) {
      pos.push(cx * u, v, sz * u);
      nor.push(nx, 0, nz);
      uv.push(u + 0.5, v);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}

/** Procedural foliage atlas (grey + alpha), no canvas so it also runs headless. u across, v along.
 *  The lower half (v 0..0.5) is the conifer needle spray; the upper half (v 0.5..1) is one side of
 *  a palm frond: a comb of long narrow leaflets that leave the rib (u = 0) and sweep forward. */
export function makeNeedleTexture(): THREE.DataTexture {
  const W = 128, H = 128;
  const px = new Uint8Array(W * H * 2 * 4);
  let rowOff = 0;                                              // 0 = needle half, H = palm half
  let s = 0x2545f491;
  const rnd = () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
  const stamp = (x: number, y: number, r: number, lum: number) => {
    const x0 = Math.max(0, Math.floor(x - r - 1)), x1 = Math.min(W - 1, Math.ceil(x + r + 1));
    const y0 = Math.max(0, Math.floor(y - r - 1)), y1 = Math.min(H - 1, Math.ceil(y + r + 1));
    for (let yy = y0; yy <= y1; yy++) for (let xx = x0; xx <= x1; xx++) {
      const d = Math.hypot(xx + 0.5 - x, yy + 0.5 - y);
      const a = Math.max(0, Math.min(1, r + 0.5 - d));
      if (a <= 0) continue;
      const o = ((yy + rowOff) * W + xx) * 4;
      const na = Math.round(a * 255);
      if (na >= px[o + 3]) { px[o] = px[o + 1] = px[o + 2] = lum; px[o + 3] = na; }
    }
  };
  const line = (x0: number, y0: number, x1: number, y1: number, r: number, lum: number) => {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 1.5);
    for (let i = 0; i <= n; i++) { const t = i / n; stamp(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, r, lum); }
  };
  // Row 0 of a DataTexture is v = 0 (the base), so y grows toward the tip.
  line(W / 2, 0, W / 2, H - 1, 1.3, 120);                       // twig
  const N = 26;
  for (let i = 0; i < N; i++) {
    const v = 0.04 + 0.9 * (i / (N - 1));
    for (const side of [-1, 1]) {
      const ang = (34 + rnd() * 26) * Math.PI / 180;            // from the axis, sweeping forward
      const len = (0.20 + rnd() * 0.16) * (1.0 - 0.35 * v) * H;
      const x0 = W / 2 + side * 1.5, y0 = v * H;
      const lum = Math.round(150 + 105 * (0.4 + 0.6 * v) * (0.7 + 0.3 * rnd()));
      line(x0, y0, x0 + side * Math.sin(ang) * len, y0 + Math.cos(ang) * len, 1.15, lum);
    }
  }
  // Palm half. Leaflets are drawn tapering from base to tip (radius shrinks along the line), longest
  // mid-frond and shorter toward the card's far end, each sweeping 20 to 34 degrees forward.
  rowOff = H;
  const taper = (x0: number, y0: number, x1: number, y1: number, r0: number, r1: number, lum: number) => {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 1.5);
    for (let i = 0; i <= n; i++) { const t = i / n; stamp(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, r0 + (r1 - r0) * t, lum); }
  };
  line(0.5, 0, 0.5, H - 1, 1.4, 105);                           // the rib's edge
  const NP = 26;
  for (let i = 0; i < NP; i++) {
    const v = 0.02 + 0.96 * (i / (NP - 1));
    const sweep = (20 + rnd() * 14) * Math.PI / 180;
    const len = (0.97 - 0.40 * v) * (W - 2) * (0.88 + 0.12 * rnd());
    const x0 = 1.0, y0 = v * H;
    const lum = Math.round(165 + 90 * (0.5 + 0.5 * rnd()));
    taper(x0, y0, x0 + len * Math.cos(sweep * 0.6), y0 + len * Math.sin(sweep), 2.0, 0.8, lum);
  }
  rowOff = 0;
  const tex = new THREE.DataTexture(px, W, H * 2, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}

// ---------------------------------------------------------------------------------------
// Materials: MeshStandardMaterial with the vertex placement replaced (keeps lights, fog, tone
// mapping). Chunk names below are stable across three r130..r18x.
// ---------------------------------------------------------------------------------------
/** The palm textures, shared by the wood and sprig materials. They decode after construction; until then the `ready` uniforms stay 0 and
 *  the shaders fall back to the plain look, so nothing flashes black. */
export interface PalmTexUniforms {
  uPalmLeaf: { value: THREE.Texture | null }; uPalmMean: { value: THREE.Vector3 }; uPalmReady: { value: number };
  uBark: { value: THREE.Texture | null }; uBarkMean: { value: THREE.Vector3 }; uBarkReady: { value: number };
}
const BARK_FLAG_PALM = 128;
const PALM_WIN = 0.667;           // the part of the leaflet sheet one card shows (it is 1.5 x as tall as it is wide, a card is about square)
const WOOD_END_OVERLAP = 1.0;   // how far a wood segment runs past each end, in its own radii (hides the seams at bends)
const BARK_BUMP = 5.0;           // strength of the bark relief
const BARK_V_PER_C = 2.0;        // bark tiles per trunk circumference of height: more = finer rings
export function makePalmTextures(): PalmTexUniforms {
  const u: PalmTexUniforms = {
    uPalmLeaf: { value: null }, uPalmMean: { value: new THREE.Vector3(...PALM_LEAF_MEAN) }, uPalmReady: { value: 0 },
    uBark: { value: null }, uBarkMean: { value: new THREE.Vector3(...PALM_BARK_MEAN) }, uBarkReady: { value: 0 },
  };
  if (typeof Image === 'undefined') return u;                  // headless: the procedural look stays
  const load = (uri: string, repeat: boolean, aniso: number, set: (t: THREE.Texture) => void) => {
    const img = new Image();
    img.onload = () => {
      const t = new THREE.Texture(img);
      t.colorSpace = THREE.SRGBColorSpace; t.flipY = false; t.generateMipmaps = true;
      t.minFilter = THREE.LinearMipmapLinearFilter; t.magFilter = THREE.LinearFilter; t.anisotropy = aniso;
      t.wrapS = t.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
      t.needsUpdate = true; set(t);
    };
    img.src = uri;
  };
  load(PALM_LEAF_URI, false, 4, (t) => { u.uPalmLeaf.value = t; u.uPalmReady.value = 1; });
  load(PALM_BARK_URI, true, 4, (t) => { u.uBark.value = t; u.uBarkReady.value = 1; });
  return u;
}

const VERT_DECL = /* glsl */`
attribute vec3 aA; attribute vec3 aB; attribute vec3 aC; attribute vec4 aCol;
uniform sampler2D uGirth;
uniform sampler2D uTreeInfo;
varying vec3 vTreeCol;
varying vec2 vNeedleUv;
varying vec3 vCanopyPos;
` + WIND_GLSL;

export const BARK_FRAG_DECL = /* glsl */`
varying float vBark; varying vec2 vBarkDir; varying float vBarkV;
uniform sampler2D uBark; uniform vec3 uBarkMean; uniform float uBarkReady;
float gBark = 0.0; vec2 gBarkDH = vec2(0.0);
`;
// Colour: the cell's own colour times the bark's detail (its colour over its mean). Angle round the trunk comes per pixel from the
// interpolated radial direction, so there is no seam, and the texture gradients are worked out from it by hand for the same reason.
export const BARK_COLOR = /* glsl */`
diffuseColor.rgb *= vTreeCol;
{
  vec2 bd = vBarkDir; float bl2 = max(dot(bd, bd), 1e-6);
  vec2 bdx = dFdx(bd), bdy = dFdy(bd);
  vec2 bGx = vec2((bd.x * bdx.y - bd.y * bdx.x) / bl2 * 0.15915494, dFdx(vBarkV));
  vec2 bGy = vec2((bd.x * bdy.y - bd.y * bdy.x) / bl2 * 0.15915494, dFdy(vBarkV));
  float bh = 0.5;
  if (vBark < 0.75 && uBarkReady > 0.5) {
    vec4 bs = textureGrad(uBark, vec2(atan(bd.y, bd.x) * 0.15915494 + 0.5, vBarkV), bGx, bGy);
    diffuseColor.rgb *= clamp(bs.rgb / uBarkMean, 0.2, 2.2);
    bh = bs.a; gBark = 1.0;
  }
  gBarkDH = vec2(dFdx(bh), dFdy(bh)) * ${BARK_BUMP.toFixed(2)};
}
`;
export const BARK_NORMAL = /* glsl */`
{
  vec3 bSX = normalize(dFdx(-vViewPosition)), bSY = normalize(dFdy(-vViewPosition));
  vec3 bR1 = cross(bSY, normal), bR2 = cross(normal, bSX);
  float bDet = dot(bSX, bR1) * faceDirection;
  vec3 bGrad = sign(bDet) * (gBarkDH.x * bR1 + gBarkDH.y * bR2);
  if (gBark > 0.5) normal = normalize(abs(bDet) * normal - bGrad);
}
`;

function makeWoodMaterial(girth: THREE.Texture, info: THREE.Texture, wind: WindField, shade: CanopyShade, palm: PalmTexUniforms): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ roughness: 0.92, metalness: 0.0 });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uGirth = { value: girth };
    sh.uniforms.uTreeInfo = { value: info };
    Object.assign(sh.uniforms, wind.uniforms, shade.uniforms, { uBark: palm.uBark, uBarkMean: palm.uBarkMean, uBarkReady: palm.uBarkReady });
    sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying float vBark;\nvarying vec2 vBarkDir;\nvarying float vBarkV;\n' + VERT_DECL)
        .replace('#include <beginnormal_vertex>', /* glsl */`
        vec4 tInfo = texelFetch(uTreeInfo, ivec2(int(aC.z), 0), 0);
        vec2 tPush = windPush(tInfo.ba);                      // one gust evaluation per tree (at its trunk)
        vec3 sA = aA + treeSwayAt(aA, tInfo.rg, aC.z, tPush);
        vec3 sB = aB + treeSwayAt(aB, tInfo.rg, aC.z, tPush);
        vec3 tAxis = sB - sA;
        float tLen = max(length(tAxis), 1e-5);
        vec3 tY = tAxis / tLen;
        vec3 tRef = abs(tY.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
        vec3 tX = normalize(cross(tRef, tY));
        vec3 tZ = cross(tY, tX);
        float tG = texelFetch(uGirth, ivec2(int(aC.z), 0), 0).r;
        vec3 objectNormal = tX * position.x + tZ * position.z;
      `)
        .replace('#include <begin_vertex>', /* glsl */`
        float tRad = mix(aC.x, aC.y, position.y) * tG;
        // Each end runs on by about one radius, so the tubes of two segments overlap through a bend instead of leaving a wedge of sky between them.
        float tExt = min(max(aC.x, aC.y) * tG * ${WOOD_END_OVERLAP.toFixed(2)}, 0.5 * tLen);
        vec3 transformed = sA + tY * (position.y * (tLen + 2.0 * tExt) - tExt) + (tX * position.x + tZ * position.z) * tRad;
        float tVar = 0.86 + 0.28 * fract(sin(float(gl_InstanceID) * 12.9898) * 43758.5453);
        vTreeCol = pow(aCol.rgb, vec3(2.2)) * tVar;
        vCanopyPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
        // bark (palm trunks, flagged in the colour's alpha): a frame that is the same on every segment of a trunk, so the rings line up
        vBark = aCol.a;
        vec3 bX = normalize(vec3(1.0, 0.0, 0.0) - tY * tY.x);
        vec3 bZ = cross(tY, bX);
        vec3 bRad = tX * position.x + tZ * position.z;
        vBarkDir = vec2(dot(bRad, bX), dot(bRad, bZ));
        vBarkV = vCanopyPos.y * ${BARK_V_PER_C.toFixed(2)} / (6.2831853 * max(tRad, 0.01));
      `);
    sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vTreeCol;\nvarying vec2 vNeedleUv;\nvarying vec3 vCanopyPos;\n' + BARK_FRAG_DECL + CANOPY_SHADE_DECL)
        .replace('#include <color_fragment>', BARK_COLOR)
        .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n' + BARK_NORMAL)
        .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>\n${canopyShadeApply('vCanopyPos')}`);
  };
  return m;
}

/**
 * The wood's depth material, for the sun's (and the flashlight's) shadow pass. The wood is placed by its own vertex
 * shader (endpoints, girth, sway), so the stock depth material would draw every segment as a unit tube at the origin.
 * This one places a segment exactly as makeWoodMaterial does, so a trunk's shadow follows its sway and its girth.
 * A segment whose drawn radius is under the limit for the map being drawn (see WOOD_SHADOW_TEXEL_K) collapses to a point (zero-area triangles, nothing rasterized):
 * wood thinner than a shadow texel would only make the map flicker, and boughs, rachises and twigs are the bulk of the wood.
 */
function makeWoodDepthMaterial(girth: THREE.Texture, info: THREE.Texture, wind: WindField): THREE.MeshDepthMaterial {
  const m = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uGirth = { value: girth };
    sh.uniforms.uTreeInfo = { value: info };
    sh.uniforms.uShadowMinR = { value: WOOD_SHADOW_MIN_R };
    sh.uniforms.uShadowMinRFine = { value: WOOD_SHADOW_MIN_R_FINE };
    sh.uniforms.uShadowTexelK = { value: WOOD_SHADOW_TEXEL_K };
    sh.uniforms.uShadowPx = { value: SHADOW_MAP_PX };
    Object.assign(sh.uniforms, wind.uniforms);
    sh.vertexShader = sh.vertexShader
        .replace('#include <common>', /* glsl */`#include <common>
attribute vec3 aA; attribute vec3 aB; attribute vec3 aC;
uniform sampler2D uGirth;
uniform sampler2D uTreeInfo;
uniform float uShadowMinR;
uniform float uShadowMinRFine;
uniform float uShadowTexelK;
uniform float uShadowPx;
` + WIND_GLSL)
        .replace('#include <begin_vertex>', /* glsl */`
        vec4 tInfo = texelFetch(uTreeInfo, ivec2(int(aC.z), 0), 0);
        vec2 tPush = windPush(tInfo.ba);
        vec3 sA = aA + treeSwayAt(aA, tInfo.rg, aC.z, tPush);
        vec3 sB = aB + treeSwayAt(aB, tInfo.rg, aC.z, tPush);
        vec3 tAxis = sB - sA;
        float tLen = max(length(tAxis), 1e-5);
        vec3 tY = tAxis / tLen;
        vec3 tRef = abs(tY.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
        vec3 tX = normalize(cross(tRef, tY));
        vec3 tZ = cross(tY, tX);
        float tG = texelFetch(uGirth, ivec2(int(aC.z), 0), 0).r;
        float tRad = mix(aC.x, aC.y, position.y) * tG;
        vec3 transformed = sA + tY * (position.y * tLen) + (tX * position.x + tZ * position.z) * tRad;
        float minR = uShadowMinR;
        if (projectionMatrix[3][3] > 0.5) {       // a cascade (orthographic): its texel is 2 / (scale * map size)
          float texel = 2.0 / (projectionMatrix[0][0] * uShadowPx);
          minR = clamp(uShadowTexelK * texel, uShadowMinRFine, uShadowMinR);
        }
        if (max(aC.x, aC.y) * tG < minR) transformed = sA;
      `);
  };
  m.customProgramCacheKey = () => 'frozen-wood-depth-v2';
  return m;
}

// `sizeMul` scales every card: the LOD variants draw fewer sprigs, each larger (see SPRIG_LOD).
function makeSprigMaterial(girth: THREE.Texture, info: THREE.Texture, needle: THREE.Texture, wind: WindField, shade: CanopyShade, sizeMul: number, palm: PalmTexUniforms): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    roughness: 0.85, metalness: 0.0, side: THREE.DoubleSide, alphaTest: 0.42,
  });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uGirth = { value: girth };
    sh.uniforms.uTreeInfo = { value: info };
    sh.uniforms.uNeedle = { value: needle };
    sh.uniforms.uSprigSizeMul = { value: sizeMul };
    Object.assign(sh.uniforms, wind.uniforms, shade.uniforms, { uPalmLeaf: palm.uPalmLeaf, uPalmMean: palm.uPalmMean, uPalmReady: palm.uPalmReady });
    sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vCrownN;\nvarying float vPalm;\nvarying vec3 vPalmUv;\nuniform float uSprigSizeMul;\n' + VERT_DECL)
        .replace('#include <beginnormal_vertex>', /* glsl */`
        vec3 tY = normalize(aB);
        vec3 tRef = abs(tY.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
        vec3 tX0 = normalize(cross(tRef, tY));
        vec3 tZ0 = cross(tY, tX0);
        // aC.y carries the card's random number (its fraction) and, from 2.0 up, the palm-frond flag
        float tPalm = step(2.0, aC.y);
        float tProg = max(floor(aC.y) - 2.0, 0.0) / 15.0;        // how far out along its frond this card's segment ends, 0..1
        float tRoll = 6.2831853 * fract(aC.y);
        vec3 tX = tX0 * cos(tRoll) + tZ0 * sin(tRoll);
        vec3 tZ = tZ0 * cos(tRoll) - tX0 * sin(tRoll);
        float tG = texelFetch(uGirth, ivec2(int(aC.z), 0), 0).r;
        // Two normals. The lit normal is the old up-biased one: even brightness all round the
        // crown, bright tops and dark undersides. The crown normal (out from the trunk axis,
        // tilted up toward the top) is not used for diffuse; it goes to the fragment shader to
        // place the sun sheen on the sun-facing side only.
        vec3 cardN = tX * normal.x + tZ * normal.z;
        vec4 cInfo = texelFetch(uTreeInfo, ivec2(int(aC.z), 0), 0);
        vec2 cRad = aA.xz - cInfo.ba;
        float cRadLen = length(cRad);
        vec2 cOut = cRad / max(cRadLen, 1e-4);
        float cOutW = smoothstep(0.0, 0.5, cRadLen);             // near the trunk the outward sense is undefined
        float cHf = clamp((aA.y - cInfo.r) / max(cInfo.g - cInfo.r, 1e-3), 0.0, 1.0);
        vec3 crownN = normalize(vec3(cOut.x * cOutW, mix(0.35, 0.9, cHf) + (1.0 - cOutW), cOut.y * cOutW));
        vCrownN = normalize(normalMatrix * normalize(crownN + cardN * 0.25));
        vec3 objectNormal = normalize(cardN * 0.45 + tY * 0.35 + vec3(0.0, 0.85, 0.0));
      `)
        .replace('#include <begin_vertex>', /* glsl */`
        float tSize = aC.x * (0.6 + 0.4 * tG) * uSprigSizeMul;
        vec2 sPush = windPush(cInfo.ba);                      // one gust evaluation per tree (at its trunk)
        vec3 sAnchor = aA + treeSwayAt(aA, cInfo.rg, aC.z, sPush);
        vec3 transformed = sAnchor + tY * (position.y * tSize)
                         + (tX * position.x + tZ * position.z) * (tSize * 0.95);
        vNeedleUv = vec2(uv.x, uv.y * 0.5);
        vPalm = tPalm;
        vPalmUv = vec3(0.0);
        if (tPalm > 0.5) {
          // Palm frond: two of the three crossed planes become the two halves of a V that hangs
          // either side of the rib; the third collapses to nothing.
          float pk = normal.x > -0.1 ? 0.0 : (normal.z > 0.0 ? 1.0 : 2.0);
          vec3 pUp = vec3(0.0, 1.0, 0.0);
          vec3 pSide = abs(tY.y) > 0.95 ? tX : normalize(cross(tY, pUp));
          float pD = uv.x;                                         // 0 at the rib, 1 at the leaflet tips
          float pTip = tProg - ${PALM_SEG_FRAC.toFixed(2)} + uv.y * ${PALM_CARD_SPAN.toFixed(2)};         // where this point of the card lies along the frond
          float pTaper = mix(1.0, ${PALM_TIP_MIN.toFixed(2)}, smoothstep(${PALM_TIP_FROM.toFixed(2)}, ${PALM_TIP_END.toFixed(2)}, pTip));
          float pW = tSize * ${PALM_SHEET_WIDTH.toFixed(2)} * pTaper;
          float pHang = ${PALM_SHEET_HANG.toFixed(2)} * (0.8 + 0.4 * fract(aC.y * 7.31));
          transformed = sAnchor + tY * (position.y * tSize)
                      + pSide * ((pk < 0.5 ? -1.0 : 1.0) * pD * pW)
                      - pUp * (pD * (0.35 + 0.65 * pD) * pW * pHang);
          if (pk > 1.5) transformed = sAnchor;
          vNeedleUv = vec2(pD, 0.5 + uv.y * 0.5);
          // the real frond's sheet: each card shows its own stretch of it (and each half of the V a different one), so the cards do not repeat
          vPalmUv = vec3(pD, fract(aC.y * 17.13 + pk * 0.381) * ${(1 - PALM_WIN).toFixed(3)} + uv.y * ${PALM_WIN.toFixed(3)}, uv.y);
        }
        // needles stream a little further downwind and flutter on their own
        {
          float sFl = sin(uWindT * (4.0 + 3.0 * aC.y) + aC.y * 40.0 + aA.x * 1.3 + aA.z * 0.9);
          vec2 sPerp = vec2(-uWindDir.y, uWindDir.x);
          float sy2 = position.y * position.y;
          transformed.xz += (sPush * 0.10 + sPerp * (0.06 * sFl * length(sPush))) * tSize * sy2;
        }
        float tVar = 0.82 + 0.30 * fract(sin(float(gl_InstanceID) * 78.233) * 43758.5453);
        vTreeCol = pow(aCol.rgb, vec3(2.2)) * tVar;
        vCanopyPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
      `);
    sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D uNeedle;\nuniform sampler2D uPalmLeaf;\nuniform vec3 uPalmMean;\nuniform float uPalmReady;\nvarying float vPalm;\nvarying vec3 vPalmUv;\nvarying vec3 vTreeCol;\nvarying vec2 vNeedleUv;\nvarying vec3 vCrownN;\nvarying vec3 vCanopyPos;\n' + CANOPY_SHADE_DECL)
        // Replace the material's own specular (driven by the up-biased lit normal, so it glinted
        // on whichever side the eye and sun lined up with the sky) with a sheen from the crown
        // normal: nonzero only where the crown faces the sun and the eye sits near the mirror
        // direction, i.e. on the sun-facing side of the tree.
        .replace('#include <lights_fragment_end>', /* glsl */`
        #include <lights_fragment_end>
        reflectedLight.indirectSpecular = vec3(0.0);
        reflectedLight.directSpecular = vec3(0.0);
        #if NUM_DIR_LIGHTS > 0
        {
          vec3 shL = normalize(directionalLights[0].direction);
          vec3 shH = normalize(shL + normalize(vViewPosition));
          vec3 shN = normalize(vCrownN);
          float shS = pow(max(dot(shN, shH), 0.0), ${SHEEN_POWER.toFixed(1)}) * max(dot(shN, shL), 0.0);
          // tint toward the needle's own hue so the sheen brightens the green rather than greying it
          vec3 shTint = mix(vec3(1.0), diffuseColor.rgb / max(max(diffuseColor.r, max(diffuseColor.g, diffuseColor.b)), 1e-3), 0.6);
          reflectedLight.directSpecular = directionalLights[0].color * shTint * (${SHEEN_STRENGTH.toFixed(3)} * shS * needleS.r);
        }
        #endif
        ${canopyShadeApply('vCanopyPos')}
      `)
        .replace('#include <color_fragment>', /* glsl */`
        vec2 nDx = dFdx(vNeedleUv), nDy = dFdy(vNeedleUv), pDx = dFdx(vPalmUv.xy), pDy = dFdy(vPalmUv.xy);
        vec4 needleS; vec3 leafMul;
        if (vPalm > 0.5 && uPalmReady > 0.5) {
          // a palm frond card: the real frond's colour over its mean, so the cell's own green still sets the hue
          vec4 ps = textureGrad(uPalmLeaf, vPalmUv.xy, pDx, pDy);
          ps.a *= smoothstep(0.0, 0.12, vPalmUv.z) * (1.0 - smoothstep(0.86, 1.0, vPalmUv.z));   // leaflets thin out toward a card's ends, where its neighbours take over
          leafMul = clamp(ps.rgb / uPalmMean, 0.3, 2.2) * 1.15;
          needleS = vec4(clamp(dot(leafMul, vec3(0.3333)) * 0.75, 0.0, 1.0), 0.0, 0.0, ps.a);
        } else {
          needleS = textureGrad(uNeedle, vNeedleUv, nDx, nDy);
          leafMul = needleS.rgb * 1.35;
        }
        diffuseColor.a *= needleS.a;
        diffuseColor.rgb *= vTreeCol * leafMul;
      `)
        // Double-sided cards would flip the normal on back faces, making half of them dark.
        .replace('#include <normal_fragment_begin>', '#include <normal_fragment_begin>\nnormal = normalize(vNormal);');
  };
  return m;
}

// ---------------------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------------------
export class FrozenTreeStore {
  private readonly maxCells: number;
  private readonly scene: THREE.Scene;
  private readonly segGeo = makeSegmentGeometry();
  private readonly sprigGeo = makeSprigGeometry();
  private readonly needleTex = makeNeedleTexture();
  private readonly palmTex = makePalmTextures();
  private readonly girthData = new Float32Array(GIRTH_TEX_W);
  private readonly girthTex: THREE.DataTexture;
  readonly wind: WindField;
  private readonly ownsWind: boolean;
  readonly shade: CanopyShade;
  private readonly ownsShade: boolean;
  private readonly treeBase = new Float32Array(GIRTH_TEX_W).fill(1e6);    // lowest cell y seen, per tree
  private readonly treeTop = new Float32Array(GIRTH_TEX_W).fill(-1e6);    // highest cell y seen, per tree
  // per tree: r = base y, g = top y, b = axis x, a = axis z (the trunk's position at its lowest cell)
  private readonly infoData = new Float32Array(GIRTH_TEX_W * 4);
  private readonly infoTex: THREE.DataTexture;
  private infoDirty = true;
  private readonly woodMat: THREE.MeshStandardMaterial;
  private readonly woodDepth: THREE.MeshDepthMaterial;       // the wood's shadow-pass material
  private woodShadows: boolean;                                 // trunks and heavy boughs cast into the shadow maps
  private readonly sprigMats: THREE.MeshStandardMaterial[];   // one per SPRIG_LOD level

  // spatial bins (see the header): each has its own wood and sprig pool
  private readonly bins: Bin[] = [];
  private readonly binByKey = new Map<number, number>();
  private enabled = true;

  // cell table: one row per frozen cid
  private readonly cidToCell = new Map<number, number>();
  private cellWood: Int32Array = new Int32Array(1024).fill(-1);    // index within the cell's bin's wood pool
  private cellSprig: Int32Array = new Int32Array(1024).fill(-1);   // index within the cell's bin's sprig pool
  private cellBin: Int32Array = new Int32Array(1024).fill(-1);     // the bin holding the cell's instances
  private cellThin: Uint8Array = new Uint8Array(1024);             // 1 when the cell's wood is in the bin's twig pool
  private cellCid: Uint32Array = new Uint32Array(1024);
  private freeCells: number[] = [];
  private nCells = 0;

  // per-tree girth
  private readonly ownerIdx = new Map<number, number>();
  private readonly maturity = new Float32Array(GIRTH_TEX_W);   // highest stem progress seen, 0..1
  private readonly girthCur = new Float32Array(GIRTH_TEX_W).fill(-1);
  private nOwners = 0;
  private girthDirty = false;

  private readonly onWoodMove = (cell: number, idx: number) => { this.cellWood[cell] = idx; };
  private readonly onSprigMove = (cell: number, idx: number) => { this.cellSprig[cell] = idx; };

  /** `wind`: the shared field (the caller advances it); omitted, the store makes and advances its own. */
  constructor(scene: THREE.Scene, maxCells: number, wind?: WindField, shade?: CanopyShade) {
    this.scene = scene;
    this.maxCells = maxCells;
    this.ownsWind = !wind;
    this.ownsShade = !shade;
    this.shade = shade ?? new CanopyShade(1, 1);
    this.wind = wind ?? new WindField();
    this.infoTex = new THREE.DataTexture(this.infoData, GIRTH_TEX_W, 1, THREE.RGBAFormat, THREE.FloatType);
    this.infoTex.minFilter = THREE.NearestFilter;
    this.infoTex.magFilter = THREE.NearestFilter;
    this.infoTex.generateMipmaps = false;
    this.infoTex.needsUpdate = true;
    for (let i = 0; i < GIRTH_TEX_W; i++) { this.infoData[i * 4] = 1e6; this.infoData[i * 4 + 1] = -1e6; }
    this.girthTex = new THREE.DataTexture(this.girthData, GIRTH_TEX_W, 1, THREE.RedFormat, THREE.FloatType);
    this.girthTex.minFilter = THREE.NearestFilter;
    this.girthTex.magFilter = THREE.NearestFilter;
    this.girthTex.generateMipmaps = false;
    this.girthTex.needsUpdate = true;
    this.woodMat = makeWoodMaterial(this.girthTex, this.infoTex, this.wind, this.shade, this.palmTex);
    this.woodDepth = makeWoodDepthMaterial(this.girthTex, this.infoTex, this.wind);
    this.woodShadows = !(typeof location !== 'undefined' && /[?&]nowoodshadow\b/.test(location.search));
    this.sprigMats = SPRIG_LOD.map(l => makeSprigMaterial(this.girthTex, this.infoTex, this.needleTex, this.wind, this.shade, l.mul, this.palmTex));
  }

  get count(): number { return this.cidToCell.size; }
  get woodCount(): number { let n = 0; for (const b of this.bins) n += b.wood.n + b.twigs.n; return n; }
  get sprigCount(): number { let n = 0; for (const b of this.bins) n += b.sprigs.n; return n; }
  /** sprigs actually submitted after LOD thinning (three's frustum culling then removes more) */
  get sprigsDrawn(): number { let n = 0; for (const b of this.bins) n += b.sprigs.drawn; return n; }
  get binCount(): number { return this.bins.length; }
  get chunkCount(): number { let n = 0; for (const b of this.bins) n += b.wood.chunkCount + b.twigs.chunkCount + b.sprigs.chunkCount; return n; }
  has(cid: number): boolean { return this.cidToCell.has(cid); }

  /** Trunks and heavy boughs cast into the shadow maps or not (see WOOD_SHADOW_MIN_R). On by default. */
  setWoodShadows(on: boolean) {
    this.woodShadows = on;
    for (const b of this.bins) b.wood.setCastShadow(on);
  }
  get woodShadowsOn(): boolean { return this.woodShadows; }
  /** Wood segments in the casting pools (before the depth shader drops the thin ones), for the overlay. */
  get woodCasting(): number { let n = 0; if (this.woodShadows) for (const b of this.bins) n += b.wood.n; return n; }

  /** Show or hide all trees (debug toggle). */
  setEnabled(e: boolean) {
    this.enabled = e;
    for (const b of this.bins) { b.wood.setEnabled(e); b.twigs.setEnabled(e && b.twigsOn); b.sprigs.setEnabled(e); }
  }

  private growCells() {
    const n = this.cellWood.length * 2;
    const w = new Int32Array(n).fill(-1); w.set(this.cellWood); this.cellWood = w;
    const s = new Int32Array(n).fill(-1); s.set(this.cellSprig); this.cellSprig = s;
    const bn = new Int32Array(n).fill(-1); bn.set(this.cellBin); this.cellBin = bn;
    const th = new Uint8Array(n); th.set(this.cellThin); this.cellThin = th;
    const c = new Uint32Array(n); c.set(this.cellCid); this.cellCid = c;
  }

  private ownerIndex(owner: number): number {
    let i = this.ownerIdx.get(owner);
    if (i === undefined) {
      i = Math.min(this.nOwners++, GIRTH_TEX_W - 1);   // overflow trees share the last slot
      this.ownerIdx.set(owner, i);
    }
    return i;
  }

  /** The bin holding three-space ground position (x, z), created on first use. */
  private binFor(x: number, z: number): Bin {
    const ix = Math.floor(x / BIN_M), iz = Math.floor(z / BIN_M);
    const key = ((ix + 4096) & 8191) * 8192 + ((iz + 4096) & 8191);
    let id = this.binByKey.get(key);
    if (id === undefined) {
      id = this.bins.length;
      const bin: Bin = {
        id, level: 0, twigsOn: true,
        wood: new InstancePool(this.scene, this.segGeo, this.woodMat, false, this.onWoodMove, this.woodDepth),
        twigs: new InstancePool(this.scene, this.segGeo, this.woodMat, false, this.onWoodMove),
        sprigs: new InstancePool(this.scene, this.sprigGeo, this.sprigMats[0], true, this.onSprigMove),
      };
      bin.wood.setEnabled(this.enabled); bin.twigs.setEnabled(this.enabled); bin.sprigs.setEnabled(this.enabled);
      bin.wood.setCastShadow(this.woodShadows);
      this.bins.push(bin);
      this.binByKey.set(key, id);
    }
    return this.bins[id];
  }

  /** Drop a cell's instances from whichever bin holds them. */
  private dropInstances(cell: number) {
    const bi = this.cellBin[cell];
    if (bi < 0) return;
    const bin = this.bins[bi];
    const w = this.cellWood[cell], s = this.cellSprig[cell];
    if (w >= 0) (this.cellThin[cell] ? bin.twigs : bin.wood).remove(w);
    if (s >= 0) bin.sprigs.remove(s);
    this.cellWood[cell] = -1; this.cellSprig[cell] = -1; this.cellBin[cell] = -1;
  }

  /**
   * Note a cell of tree `owner` at three-space height `yThree` and return the tree's slot
   * (the index the shaders use for its girth and sway data). Live tips call this too, so a
   * tree's height and sway include its still-growing parts.
   */
  observe(owner: number, yThree: number, xThree?: number, zThree?: number): number {
    const i = this.ownerIndex(owner);
    let d = false;
    if (yThree < this.treeBase[i]) {
      this.treeBase[i] = yThree; this.infoData[i * 4] = yThree; d = true;
      // The lowest cell seen sits on the trunk, so it gives the crown axis (frozen cells only;
      // live tips omit x/z and never define the axis).
      if (xThree !== undefined && zThree !== undefined) { this.infoData[i * 4 + 2] = xThree; this.infoData[i * 4 + 3] = zThree; }
    }
    if (yThree > this.treeTop[i]) { this.treeTop[i] = yThree; this.infoData[i * 4 + 1] = yThree; d = true; }
    if (d) this.infoDirty = true;
    return i;
  }

  /** Parse one wire record at `off` and (re)build its wood segment and sprig. Returns true when the cid is new. */
  add(view: DataView, off: number): boolean {
    const cid = view.getUint32(off, true);
    const owner = view.getUint32(off + 4, true);
    const x = view.getFloat32(off + 8, true), y = view.getFloat32(off + 12, true), z = view.getFloat32(off + 16, true);
    const dx = view.getInt16(off + 20, true) / 1000, dy = view.getInt16(off + 22, true) / 1000, dz = view.getInt16(off + 24, true) / 1000;
    const r0 = view.getUint16(off + 26, true) / 10000, r1 = view.getUint16(off + 28, true) / 10000;
    const ctype = view.getUint8(off + 30), prog = view.getUint8(off + 31);
    const cr = view.getUint8(off + 32), cg = view.getUint8(off + 33), cb = view.getUint8(off + 34);
    const flagsByte = view.getUint8(off + 35);
    const hasParent = (flagsByte & 1) !== 0;
    const kind = (flagsByte >> 1) & 7;
    const palm = kind === KIND_PALM, scrub = kind === KIND_SCRUB;

    let cell = this.cidToCell.get(cid);
    const isNew = cell === undefined;
    if (cell === undefined) {
      if (this.cidToCell.size >= this.maxCells) return false;
      cell = this.freeCells.length ? this.freeCells.pop()! : this.nCells++;
      if (cell >= this.cellWood.length) this.growCells();
      this.cidToCell.set(cid, cell);
      this.cellCid[cell] = cid;
      this.cellWood[cell] = -1; this.cellSprig[cell] = -1; this.cellBin[cell] = -1; this.cellThin[cell] = 0;
    }

    const pi = this.observe(owner, z, x, -y); // z is sim-up = three y; (x, -y) is three x, z
    if (ctype === CELL_STEM) {
      const m = prog / 255;
      if (m > this.maturity[pi]) this.maturity[pi] = m;
    }

    // sim (x,y,z) -> three (x,z,-y)
    const px = x, py = z, pz = -y;
    const ax = px + dx, ay = py + dz, az = pz - dy;     // parent end, in three space
    const isWood = ctype === CELL_STEM || ctype === CELL_BRANCH || ctype === CELL_TWIG;

    // The cell's instances live in the bin under it. A re-sent record that somehow landed in a
    // different bin is re-homed (the old instances go, the new ones are built below).
    const bin = this.binFor(px, pz);
    const thin = ctype === CELL_TWIG ? 1 : 0;
    if (this.cellBin[cell] >= 0 && (this.cellBin[cell] !== bin.id || this.cellThin[cell] !== thin)) this.dropInstances(cell);
    this.cellBin[cell] = bin.id;
    this.cellThin[cell] = thin;

    if (isWood) {
      let bx = ax, by = ay, bz = az, rb = r0;
      if (!hasParent) {                                  // root cell: flare into the ground
        bx = px; by = py - ROOT_FLARE_DEPTH; bz = pz; rb = r1 * ROOT_FLARE_SCALE;
      }
      let wr = cr, wg = cg, wb = cb;
      if (ctype !== CELL_STEM) {
        const t = palm ? PALM_RACHIS_BARK_MIX
            : ctype === CELL_TWIG ? TWIG_BARK_MIX
                : BRANCH_BARK_MIX_MIN + (BRANCH_BARK_MIX_MAX - BRANCH_BARK_MIX_MIN) * (prog / 255);
        wr = BARK_R + (cr - BARK_R) * t; wg = BARK_G + (cg - BARK_G) * t; wb = BARK_B + (cb - BARK_B) * t;
      }
      const wpool = thin ? bin.twigs : bin.wood;
      if (this.cellWood[cell] < 0) this.cellWood[cell] = wpool.add(cell);
      wpool.set(this.cellWood[cell], [bx, by, bz], [px, py, pz], [rb, r1, pi], wr, wg, wb, palm && ctype === CELL_STEM ? BARK_FLAG_PALM : 255);
    }

    // needle sprig placement
    let sprig = false;
    let palmCard = false;                                // drawn as a pinnate frond card, not a needle spray
    let sx = px, sy = py, sz2 = pz, dirx = 0, diry = 1, dirz = 0, size = 0.2;
    const h1 = hash01(cid, 1), h2 = hash01(cid, 2), h3 = hash01(cid, 3);
    if (ctype === CELL_LEAF) {
      // fan outward from the shoot tip; a leaf with no parent stands upright
      let ox = -dx, oy = -dz, oz = dy;
      let ol = Math.hypot(ox, oy, oz);
      if (ol < 1e-4 || !hasParent) { ox = 0; oy = 1; oz = 0; ol = 1; }
      dirx = ox / ol + (h1 - 0.5) * 0.7; diry = oy / ol + (h2 - 0.5) * 0.7 - 0.1; dirz = oz / ol + (h3 - 0.5) * 0.7;
      size = Math.min(LEAF_SPRIG_MAX, Math.max(LEAF_SPRIG_MIN, r1 * LEAF_SPRIG_PER_RADIUS)) * (0.85 + 0.3 * h2);
      sprig = true;
      if (palm) {
        // the cap of a frond, or the spear leaf at the crown: longer, and held closer to the rib
        dirx = ox / ol + (h1 - 0.5) * 0.35; diry = oy / ol + (h2 - 0.5) * 0.35 - 0.2; dirz = oz / ol + (h3 - 0.5) * 0.35;
        size = Math.min(PALM_LEAF_MAX, Math.max(PALM_LEAF_MIN, r1 * PALM_LEAF_PER_RADIUS)) * (0.85 + 0.3 * h2);
        palmCard = true;
        // foliage cells along a frond exist for the canopy's shade; the segment cards draw the frond
        if (prog === PALM_FOLIAGE_PROG) sprig = false;
      }
    } else if (hasParent && (ctype === CELL_TWIG ||
        (ctype === CELL_BRANCH && prog >= (palm ? PALM_SPRIG_FROM : scrub ? SCRUB_SPRIG_FROM : BRANCH_SPRIG_FROM) * 255) ||
        (ctype === CELL_STEM && !palm &&
            prog >= (scrub ? SCRUB_STEM_FROM : STEM_SPRIG_FROM) * 255 &&
            h1 < (scrub ? SCRUB_STEM_CHANCE : STEM_SPRIG_CHANCE)))) {
      // a sleeve of needles over the segment, from its parent end outward
      let ox = px - ax, oy = py - ay, oz = pz - az;
      const len = Math.hypot(ox, oy, oz) || 1;
      ox /= len; oy /= len; oz /= len;
      sx = ax; sy = ay; sz2 = az;
      if (ctype === CELL_STEM) {                         // upper trunk: tufts angled out and up
        dirx = ox + (h2 - 0.5) * 1.6; diry = oy * 0.6 + 0.2; dirz = oz + (h3 - 0.5) * 1.6;
        size = 0.16 + 0.06 * h2;
      } else if (palm) {                                  // frond: a card along the rib; its leaflets hang either side
        dirx = ox; diry = oy; dirz = oz;
        size = Math.max(PALM_SPRIG_MIN, len * PALM_SPRIG_PER_LEN) * (0.9 + 0.25 * h3);
        palmCard = true;
      } else {
        dirx = ox + (h2 - 0.5) * 0.35; diry = oy + (h3 - 0.5) * 0.35; dirz = oz + (h1 - 0.5) * 0.35;
        size = Math.max(TWIG_SPRIG_MIN, len * 1.5) * (0.9 + 0.25 * h3);
      }
      sprig = true;
    }

    if (sprig) {
      const dl = Math.hypot(dirx, diry, dirz) || 1;
      if (this.cellSprig[cell] < 0) this.cellSprig[cell] = bin.sprigs.add(cell);
      bin.sprigs.set(this.cellSprig[cell], [sx, sy, sz2], [dirx / dl, diry / dl, dirz / dl],
          [size, h1 * 0.999 + (palmCard ? 2 + Math.min(15, Math.floor(prog / 17)) : 0), pi], cr, cg, cb);
    }
    return isNew;
  }

  /** Remove a cell (promoted or keyframe-diffed away). Returns true when we held it. */
  remove(cid: number): boolean {
    const cell = this.cidToCell.get(cid);
    if (cell === undefined) return false;
    this.dropInstances(cell);
    this.cidToCell.delete(cid);
    this.freeCells.push(cell);
    return true;
  }

  /**
   * Remove every held cell whose cid is not in `keep`. A keyframe is the full active set, so
   * this makes a keyframe a diff against what the store already holds instead of a clear and a
   * rebuild of every cell (frozen cells are immutable, so a cid we hold needs no rewrite).
   * Returns how many cells were removed.
   */
  retainOnly(keep: Set<number>): number {
    const gone: number[] = [];
    for (const cid of this.cidToCell.keys()) if (!keep.has(cid)) gone.push(cid);
    for (const cid of gone) this.remove(cid);
    return gone.length;
  }

  clear() {
    this.cidToCell.clear();
    this.freeCells.length = 0;
    this.nCells = 0;
    this.cellWood.fill(-1); this.cellSprig.fill(-1); this.cellBin.fill(-1); this.cellThin.fill(0);
    for (const b of this.bins) { b.wood.clear(); b.twigs.clear(); b.sprigs.clear(); }
    this.ownerIdx.clear(); this.nOwners = 0;
    this.maturity.fill(0); this.girthCur.fill(-1);
    this.girthDirty = true;
    this.treeBase.fill(1e6); this.treeTop.fill(-1e6);
    for (let i = 0; i < GIRTH_TEX_W; i++) {
      this.infoData[i * 4] = 1e6; this.infoData[i * 4 + 1] = -1e6;
      this.infoData[i * 4 + 2] = 0; this.infoData[i * 4 + 3] = 0;
    }
    this.infoDirty = true;
  }

  /** Commit this frame's additions. Call once after parsing a frozen section. */
  flush() { for (const b of this.bins) { b.wood.flush(); b.twigs.flush(); b.sprigs.flush(); } }

  /**
   * Pick each bin's sprig LOD from its distance to the eye. Call every frame (cheap: a few
   * dozen bins). Levels switch with a little hysteresis so a bin on a boundary does not flicker.
   */
  setView(cam: THREE.Vector3) {
    for (const b of this.bins) {
      const d = b.sprigs.distanceTo(cam);
      if (d !== Infinity) {
        let lvl = b.level;
        while (lvl + 1 < SPRIG_LOD.length && d > SPRIG_LOD[lvl + 1].from * (1 + LOD_HYST)) lvl++;
        while (lvl > 0 && d < SPRIG_LOD[lvl].from * (1 - LOD_HYST)) lvl--;
        if (lvl !== b.level) { b.level = lvl; b.sprigs.setMaterial(this.sprigMats[lvl]); }
        b.sprigs.setFrac(SPRIG_LOD[lvl].frac);
      }
      // twig wood: on near, off far, with the same hysteresis
      const dt = b.twigs.distanceTo(cam);
      if (dt !== Infinity) {
        const on = b.twigsOn ? dt < THIN_WOOD_FAR * (1 + LOD_HYST) : dt < THIN_WOOD_FAR * (1 - LOD_HYST);
        if (on !== b.twigsOn) { b.twigsOn = on; b.twigs.setEnabled(this.enabled && on); }
      }
    }
  }

  static girthFor(maturity: number): number {
    return GIRTH_MIN + (1 - GIRTH_MIN) * Math.pow(Math.min(1, Math.max(0, maturity)), GIRTH_EXP);
  }

  /** Ease each tree's girth toward its target. Cheap: one float per tree. Call every frame. */
  update(dt: number) {
    if (this.ownsWind) this.wind.update(dt);
    if (this.infoDirty) { this.infoTex.needsUpdate = true; this.infoDirty = false; }
    const k = 1 - Math.exp(-Math.max(0, dt) * GIRTH_RATE);
    for (let i = 0; i < this.nOwners && i < GIRTH_TEX_W; i++) {
      const target = FrozenTreeStore.girthFor(this.maturity[i]);
      let cur = this.girthCur[i];
      cur = cur < 0 ? target : cur + (target - cur) * k;   // first sight snaps (no grow-from-zero on connect)
      if (Math.abs(cur - this.girthData[i]) > 1e-4) { this.girthData[i] = cur; this.girthDirty = true; }
      this.girthCur[i] = cur;
    }
    if (this.girthDirty) { this.girthTex.needsUpdate = true; this.girthDirty = false; }
  }

  dispose() {
    for (const b of this.bins) { b.wood.dispose(); b.twigs.dispose(); b.sprigs.dispose(); }
    this.bins.length = 0; this.binByKey.clear();
    this.segGeo.dispose(); this.sprigGeo.dispose();
    this.woodMat.dispose(); this.woodDepth.dispose(); for (const m of this.sprigMats) m.dispose();
    this.needleTex.dispose(); this.girthTex.dispose(); this.infoTex.dispose();
    this.palmTex.uPalmLeaf.value?.dispose(); this.palmTex.uBark.value?.dispose();
    if (this.ownsShade) this.shade.dispose();
    this.cidToCell.clear();
  }
}